import React from 'react';
import { Box, Chip } from '@mui/material';
import AgentMarkdown from './AgentMarkdown';
import { getSessionMeta } from '../agent/sessionContext';

interface Props {
  role: 'user' | 'assistant' | 'error';
  text: string;
}

// エージェントの返答に含まれる予約番号（LP: TR-XXXXXX-XXXXXX / OTA: R0〜 / レンナビ: RN〜）。
// 「開きますか？」に文字で答えなくてもワンタップで予約セッションへ入れるようにする
// （2026-08-29 セッション#230 の要望「開く時、ボタンがあるといいですね」）。
// 宮古の自社サイトは PI-（2026-10-06 実データで確認）。複数台の枝番 -1/-2 も含める
const CONTACT_CODE_RE = /(?:TR|PI|PT)-[A-Z0-9]{6}-[A-Z0-9]{6}(?:-\d{1,2})?|RN\d{6}-\d{4}-\d{5}|\bR0[A-Z0-9]{6}\b/g;

function extractCodes(text: string): string[] {
  const found = text.match(CONTACT_CODE_RE) || [];
  return Array.from(new Set(found)).slice(0, 2);
}

export default function AgentMessage({ role, text }: Props) {
  const isUser = role === 'user';
  const isError = role === 'error';

  // 既にその予約セッションの中にいるコードにはボタンを出さない
  const meta = getSessionMeta();
  const codes = !isUser && !isError
    ? extractCodes(text).filter((c) => !(meta.kind === 'reservation' && meta.anchor === c))
    : [];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        px: 1.5,
        my: 0.6,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.4,
          py: 0.8,
          borderRadius: 2,
          background: isError ? '#fef2f2' : isUser ? '#0ea5e9' : '#f1f5f9',
          color: isError ? '#991b1b' : isUser ? '#fff' : '#0f172a',
          border: isError ? '1px solid #fecaca' : 'none',
          fontSize: 13,
          wordBreak: 'break-word',
        }}
      >
        {isUser ? (
          <Box sx={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{text}</Box>
        ) : (
          <Box sx={{ fontSize: 13 }}>
            <AgentMarkdown text={text} />
            {codes.length > 0 && (
              <Box sx={{ mt: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {codes.map((code) => (
                  <Chip
                    key={code}
                    size="small"
                    label={`📋 ${code} のセッションを開く`}
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent('agent:open-reservation-session', {
                          detail: { contact_code: code },
                        }),
                      )
                    }
                    sx={{ fontSize: 11, height: 22, cursor: 'pointer', bgcolor: '#e0e7ff', '&:hover': { bgcolor: '#c7d2fe' } }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
